<template>
  <div class="recaptcha-v3-wrapper">
    <!-- 當未配置 siteKey 且為開發環境時，顯示提示 -->
    <div v-if="!siteKey && isDev" class="recaptcha-v3-dev-badge">
      <span class="badge-tag">🛠️ reCAPTCHA v3 開發模式</span>
      <span class="badge-text">尚未設定 <code>VITE_RECAPTCHA_SITE_KEY</code>，提交時將自動略過驗證。</span>
    </div>

    <!-- 正常模式：符合 Google 規範之隱私權與服務條款說明 -->
    <p v-else class="recaptcha-v3-notice">
      本表單受 Google reCAPTCHA v3 機制保護，並適用 Google
      <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">隱私權政策</a> 與
      <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">服務條款</a>。
    </p>

    <!-- 後端驗證錯誤顯示 -->
    <div v-if="errorMessage" class="recaptcha-v3-error">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';

const props = defineProps({
  siteKey: {
    type: String,
    default: () => (import.meta.env.VITE_RECAPTCHA_SITE_KEY || '').trim(),
  },
  action: {
    type: String,
    default: 'customer_service',
  },
  errorMessage: {
    type: String,
    default: '',
  },
});

const isDev = import.meta.env.DEV;
const isScriptLoaded = ref(false);

const loadRecaptchaV3Script = () => {
  if (!props.siteKey || typeof window === 'undefined') {
    return Promise.resolve();
  }

  if (window.grecaptcha && typeof window.grecaptcha.execute === 'function') {
    isScriptLoaded.value = true;
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const existingScript = document.querySelector(`script[src*="google.com/recaptcha/api.js?render="]`);
    if (existingScript) {
      if (window.grecaptcha) {
        window.grecaptcha.ready(() => {
          isScriptLoaded.value = true;
          resolve();
        });
      } else {
        existingScript.addEventListener('load', () => {
          window.grecaptcha?.ready(() => {
            isScriptLoaded.value = true;
            resolve();
          });
        });
      }
      return;
    }

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(props.siteKey)}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.grecaptcha) {
        window.grecaptcha.ready(() => {
          isScriptLoaded.value = true;
          resolve();
        });
      } else {
        resolve();
      }
    };
    script.onerror = (err) => {
      console.warn('Google reCAPTCHA v3 script failed to load', err);
      reject(err);
    };
    document.head.appendChild(script);
  });
};

/**
 * 執行 reCAPTCHA v3 驗證並取得 token
 * @param {string} [customAction] - 可覆寫預設 action
 * @returns {Promise<string|null>} token 字串，若未設定 key 則回傳 null
 */
const execute = async (customAction) => {
  if (!props.siteKey) {
    return null;
  }

  const act = customAction || props.action || 'customer_service';

  try {
    await loadRecaptchaV3Script();

    if (!window.grecaptcha || typeof window.grecaptcha.execute !== 'function') {
      console.warn('window.grecaptcha.execute is not available');
      return null;
    }

    return await new Promise((resolve, reject) => {
      window.grecaptcha.ready(async () => {
        try {
          const token = await window.grecaptcha.execute(props.siteKey, { action: act });
          resolve(token);
        } catch (err) {
          reject(err);
        }
      });
    });
  } catch (err) {
    console.error('Failed to execute reCAPTCHA v3:', err);
    return null;
  }
};

onMounted(() => {
  if (props.siteKey) {
    loadRecaptchaV3Script().catch(() => {});
  }
});

defineExpose({
  execute,
});
</script>

<style scoped>
.recaptcha-v3-wrapper {
  margin: 10px 0 20px;
}

.recaptcha-v3-dev-badge {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 14px;
  background-color: #f6ffed;
  border: 1px dashed #b7eb8f;
  border-radius: 6px;
  font-size: 13px;
  line-height: 1.5;
  color: #389e0d;
}

.badge-tag {
  font-weight: 600;
}

.badge-text code {
  background: #ffffff;
  padding: 2px 6px;
  border-radius: 3px;
  border: 1px solid #d9d9d9;
  font-family: monospace;
  color: #1f1f1f;
}

.recaptcha-v3-notice {
  font-size: 12px;
  line-height: 1.6;
  color: #8c8c8c;
  margin: 0;
}

.recaptcha-v3-notice a {
  color: #595959;
  text-decoration: underline;
  transition: color 0.2s ease;
}

.recaptcha-v3-notice a:hover {
  color: #1171DD;
}

.recaptcha-v3-error {
  margin-top: 6px;
  font-size: 13px;
  color: #ff4d4f;
  line-height: 1.4;
}
</style>
